import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { PLANET_CONFIGS } from '../data/planetConfigs';
import type { PlanetConfig } from '../types/planet';

/**
 * 太阳系场景引擎
 * 负责创建和管理整个 3D 太阳系场景
 *
 * 已实现：
 * - 太阳日冕光晕（Sprite + 径向渐变）
 * - 行星纹理加载（带 fallback）
 * - 小行星带粒子系统
 */
export class SolarSystem {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private planets: Map<string, THREE.Group> = new Map();
  private orbitLines: THREE.Line[] = [];
  private starField!: THREE.Points;
  private meteors!: THREE.Points;  // 流星粒子系统
  private clock: THREE.Clock;
  private timeScale: number = 1;
  private _simulationTime: number = 0;  // 累积模拟时间（秒），平滑无跳变
  private _lastFrameTime: number = 0;   // 上一帧的真实时间戳（秒）
  private selectedPlanet: string | null = null;
  private followTarget: string | null = null;  // 镜头跟踪目标
  private _isCameraAnimating: boolean = false;  // 相机是否正在动画中
  private sunLight!: THREE.PointLight;
  private textureLoader: THREE.TextureLoader;

  private onPlanetSelect: ((name: string, config: PlanetConfig) => void) | null = null;
  private onPlanetDeselect: (() => void) | null = null; // 取消选中回调

  // 加载进度回调
  private onLoadProgress: ((progress: number, total: number) => void) | null = null;
  private loadingManager: THREE.LoadingManager;

  constructor(container: HTMLElement) {
    this.clock = new THREE.Clock();
    this.loadingManager = new THREE.LoadingManager();
    this.setupLoadingManager();
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);
    this.textureLoader.crossOrigin = 'anonymous';  // ★ 支持跨域纹理加载
    this.scene = this.createScene();
    this.camera = this.createCamera(container);
    this.renderer = this.createRenderer(container);
    this.controls = this.createControls();
    this.init();
  }

  /** 创建场景 */
  private createScene(): THREE.Scene {
    const scene = new THREE.Scene();
    scene.background = null; // 透明背景，让底层摄像头视频透出来
    scene.fog = null;
    return scene;
  }

  /** 创建相机 */
  private createCamera(container: HTMLElement): THREE.PerspectiveCamera {
    const camera = new THREE.PerspectiveCamera(
      55,
      container.clientWidth / container.clientHeight,
      0.1,
      2000
    );
    camera.position.set(25, 20, 30);
    camera.lookAt(0, 0, 0);
    return camera;
  }

  /** 创建渲染器 */
  private createRenderer(container: HTMLElement): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.8;
    container.appendChild(renderer.domElement);
    return renderer;
  }

  /** 创建轨道控制 */
  private createControls(): OrbitControls {
    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 5;
    controls.maxDistance = 150;
    controls.maxPolarAngle = Math.PI * 0.95;
    return controls;
  }

  /** 初始化场景内容 */
  private init(): void {
    this.createSun();
    this.addLights();
    this.addStarField();
    this.createAsteroidBelt(); // 小行星带
    this.createPlanets();
    this.addOrbitLines();
    this.createMeteors();      // 流星动画
  }

  /** 添加光源 */
  private addLights(): void {
    const ambient = new THREE.AmbientLight(0x334466, 0.6);
    this.scene.add(ambient);

    this.sunLight = new THREE.PointLight(0xfff5e0, 3.0, 200, 1);
    this.sunLight.position.set(0, 0, 0);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 2048;
    this.sunLight.shadow.mapSize.height = 2048;
    this.scene.add(this.sunLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 15);
    this.scene.add(dirLight);
  }

  /** 添加星空背景（使用真实星空纹理） */
  private addStarField(): void {
    // 使用星空纹理球体（BackSide渲染，从内部看）
    const starSphereGeometry = new THREE.SphereGeometry(1000, 64, 64);
    const starTextureUrl = '/textures/2k_stars.jpg';
    const starTexture = this.textureLoader.load(starTextureUrl);
    starTexture.wrapS = THREE.RepeatWrapping;
    starTexture.wrapT = THREE.RepeatWrapping;

    const starSphereMaterial = new THREE.MeshBasicMaterial({
      map: starTexture,
      side: THREE.BackSide,  // 从内部看
      transparent: false,
    });
    const starSphere = new THREE.Mesh(starSphereGeometry, starSphereMaterial);
    starSphere.name = 'star_sphere';
    this.scene.add(starSphere);

    // 保留少量粒子作为动态星星（增加层次感）
    const dynamicStarCount = 500;
    const positions = new Float32Array(dynamicStarCount * 3);
    const colors = new Float32Array(dynamicStarCount * 3);
    const sizes = new Float32Array(dynamicStarCount);

    for (let i = 0; i < dynamicStarCount; i++) {
      const i3 = i * 3;
      const radius = 800 + Math.random() * 200;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      positions[i3]     = radius * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = radius * Math.cos(phi);

      // 真实星星颜色（蓝白色、黄色、红色）
      const colorChoice = Math.random();
      if (colorChoice < 0.5) {
        colors[i3] = 0.95; colors[i3 + 1] = 0.95; colors[i3 + 2] = 1.0;  // 蓝白色
      } else if (colorChoice < 0.75) {
        colors[i3] = 1.0; colors[i3 + 1] = 0.97; colors[i3 + 2] = 0.85;  // 黄色
      } else {
        colors[i3] = 0.85; colors[i3 + 1] = 0.9; colors[i3 + 2] = 1.0;  // 淡蓝色
      }

      sizes[i] = 0.3 + Math.random() * 0.8;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      size: 0.8,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      sizeAttenuation: true,
    });

    this.starField = new THREE.Points(geometry, material);
    this.scene.add(this.starField);
  }

  /** 创建小行星带（火星和木星之间） */
  private createAsteroidBelt(): void {
    const asteroidCount = 2000;
    const innerRadius = 22;
    const outerRadius = 28;
    const minSize = 0.05;
    const maxSize = 0.25;

    const positions = new Float32Array(asteroidCount * 3);
    const colors = new Float32Array(asteroidCount * 3);
    const sizes = new Float32Array(asteroidCount);

    for (let i = 0; i < asteroidCount; i++) {
      const i3 = i * 3;
      const angle = Math.random() * Math.PI * 2;
      const radius = innerRadius + Math.random() * (outerRadius - innerRadius);
      const offsetX = (Math.random() - 0.5) * 1.5;
      const offsetZ = (Math.random() - 0.5) * 1.5;
      const y = (Math.random() - 0.5) * 0.8;

      positions[i3]     = radius * Math.cos(angle) + offsetX;
      positions[i3 + 1] = y;
      positions[i3 + 2] = radius * Math.sin(angle) + offsetZ;

      const gray = 0.3 + Math.random() * 0.3;
      colors[i3]     = gray;
      colors[i3 + 1] = gray * 0.9;
      colors[i3 + 2] = gray * 0.8;

      sizes[i] = minSize + Math.random() * (maxSize - minSize);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      sizeAttenuation: true,
    });

    const asteroidBelt = new THREE.Points(geometry, material);
    asteroidBelt.name = 'asteroid_belt';
    this.scene.add(asteroidBelt);
  }

  /** 创建流星动画 */
  private createMeteors(): void {
    const meteorCount = 8;
    const positions = new Float32Array(meteorCount * 3);
    const colors = new Float32Array(meteorCount * 3);
    const velocities = new Float32Array(meteorCount * 3); // 存储速度

    for (let i = 0; i < meteorCount; i++) {
      const i3 = i * 3;
      // 随机起始位置（在场景边缘）
      positions[i3]     = (Math.random() - 0.5) * 200;
      positions[i3 + 1] = Math.random() * 100 + 20;
      positions[i3 + 2] = (Math.random() - 0.5) * 200;

      // 亮白色/黄色（流星颜色）
      colors[i3]     = 1.0;
      colors[i3 + 1] = 0.95;
      colors[i3 + 2] = 0.7;

      // 随机速度（向场景中心移动）
      velocities[i3]     = (Math.random() - 0.5) * 0.5;
      velocities[i3 + 1] = -Math.random() * 0.3;
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 1.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      sizeAttenuation: true,
    });

    const meteors = new THREE.Points(geometry, material);
    meteors.name = 'meteors';
    this.scene.add(meteors);

    // 存储速度数据到 userData
    (meteors as any).velocities = velocities;
    (meteors as any).meteorCount = meteorCount;
    this.meteors = meteors;
  }

  /** 创建太阳 */
  private createSun(): void {
    const config = PLANET_CONFIGS[0];

    // 太阳本体
    const sunGeometry = new THREE.SphereGeometry(config.radius, 64, 64);
    let sunMaterial: THREE.Material;

    if (config.textureUrl) {
      const texture = this.textureLoader.load(
        config.textureUrl,
        undefined,
        undefined,
        () => {
          console.warn(`太阳纹理加载失败，使用纯色材质`);
        }
      );
      sunMaterial = new THREE.MeshBasicMaterial({ map: texture });
    } else {
      sunMaterial = new THREE.MeshBasicMaterial({ color: config.color });
    }

    const sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
    sunMesh.name = 'Sun_mesh';

    // 太阳日冕光晕（Sprite + 径向渐变）
    const coronaCanvas = document.createElement('canvas');
    coronaCanvas.width = 256;
    coronaCanvas.height = 256;
    const coronaCtx = coronaCanvas.getContext('2d')!;
    const gradient = coronaCtx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(255, 200, 50, 1.0)');
    gradient.addColorStop(0.2, 'rgba(255, 150, 20, 0.8)');
    gradient.addColorStop(0.5, 'rgba(255, 100, 0, 0.3)');
    gradient.addColorStop(1, 'rgba(255, 50, 0, 0)');
    coronaCtx.fillStyle = gradient;
    coronaCtx.fillRect(0, 0, 256, 256);

    const coronaTexture = new THREE.CanvasTexture(coronaCanvas);
    const coronaMaterial = new THREE.SpriteMaterial({
      map: coronaTexture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const corona = new THREE.Sprite(coronaMaterial);
    corona.scale.set(config.radius * 8, config.radius * 8, 1);
    corona.name = 'corona';

    // 光晕
    const glowGeometry = new THREE.SphereGeometry(config.radius * 2.5, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xff8800,
      transparent: true,
      opacity: 0.12,
      side: THREE.BackSide,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);

    const innerGlowGeometry = new THREE.SphereGeometry(config.radius * 1.5, 32, 32);
    const innerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffcc44,
      transparent: true,
      opacity: 0.25,
      side: THREE.BackSide,
    });
    const innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);

    const sunGroup = new THREE.Group();
    sunGroup.add(sunMesh);
    sunGroup.add(corona);
    sunGroup.add(innerGlow);
    sunGroup.add(glow);
    sunGroup.name = 'Sun';

    this.scene.add(sunGroup);
    this.planets.set('Sun', sunGroup);
  }

  /** 创建所有行星 */
  private createPlanets(): void {
    for (let i = 1; i < PLANET_CONFIGS.length; i++) {
      const config = PLANET_CONFIGS[i];
      const group = this.createPlanet(config);
      this.planets.set(config.name, group);
      this.scene.add(group);
    }
  }

  /** 创建单个行星 */
  private createPlanet(config: PlanetConfig): THREE.Group {
    const group = new THREE.Group();
    group.name = config.name;

    const geometry = new THREE.SphereGeometry(config.radius, 48, 48);
    let material: THREE.Material;

    if (config.textureUrl) {
      const texture = this.textureLoader.load(
        config.textureUrl,
        undefined,
        undefined,
        () => {
          console.warn(`纹理加载失败: ${config.name}`);
          const fallbackMaterial = new THREE.MeshStandardMaterial({
            color: config.color,
            roughness: 0.65,
            metalness: 0.1,
            emissive: config.emissive || 0x000000,
            emissiveIntensity: 0.05,
          });
          const planetMesh = group.getObjectByName(`${config.name}_mesh`) as THREE.Mesh;
          if (planetMesh) planetMesh.material = fallbackMaterial;
        }
      );
      material = new THREE.MeshStandardMaterial({
        map: texture,
        roughness: 0.65,
        metalness: 0.1,
        emissive: config.emissive || 0x000000,
        emissiveIntensity: 0.05,
      });
    } else {
      material = new THREE.MeshStandardMaterial({
        color: config.color,
        roughness: 0.65,
        metalness: 0.1,
        emissive: config.emissive || 0x000000,
        emissiveIntensity: 0.05,
      });
    }

    const planet = new THREE.Mesh(geometry, material);
    planet.castShadow = true;
    planet.receiveShadow = true;
    planet.name = `${config.name}_mesh`;
    group.add(planet);

    group.rotation.x = (config.axialTilt * Math.PI) / 180 * 0.3;

    // 土星光环（使用真实纹理 + 卡西尼缝效果）
    if (config.hasRing && config.ringInner && config.ringOuter) {
      // 主光环（使用纹理）
      const ringTextureUrl = '/textures/2k_saturn_ring.png';
      const ringTexture = this.textureLoader.load(ringTextureUrl);
      ringTexture.wrapS = THREE.ClampToEdgeWrapping;
      ringTexture.wrapT = THREE.ClampToEdgeWrapping;

      const ringGeometry = new THREE.RingGeometry(
        config.ringInner,
        config.ringOuter,
        128  // 增加分段数，让光环更平滑
      );
      const ringMaterial = new THREE.MeshBasicMaterial({
        map: ringTexture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.7,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2;
      ring.name = 'ring';
      group.add(ring);

      // 卡西尼缝（在 2.4 位置，使用深色环模拟缝隙）
      const cassiniPos = 2.4;
      if (cassiniPos > config.ringInner && cassiniPos < config.ringOuter) {
        const gapGeometry = new THREE.RingGeometry(cassiniPos - 0.05, cassiniPos + 0.05, 64);
        const gapMaterial = new THREE.MeshBasicMaterial({
          color: 0x000000,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 0.8,
        });
        const gap = new THREE.Mesh(gapGeometry, gapMaterial);
        gap.rotation.x = -Math.PI / 2;
        gap.name = 'cassini_gap';
        group.add(gap);
      }
    }

    // 地球大气层和云层
    if (config.name === 'Earth') {
      // 蓝色大气层外发光（使用 FrontSide，从外部可见的辉光）
      const atmosphereGeometry = new THREE.SphereGeometry(config.radius * 1.12, 48, 48);
      const atmosphereMaterial = new THREE.MeshBasicMaterial({
        color: 0x4488ff,
        transparent: true,
        opacity: 0.35,   // ★ 提高透明度，让大气层清晰可见
        side: THREE.FrontSide,
        depthWrite: false,
      });
      const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
      atmosphere.name = 'atmosphere';
      group.add(atmosphere);

      // 大气层内圈（更柔和的蓝色光晕）
      const atmosphereInnerGeo = new THREE.SphereGeometry(config.radius * 1.04, 48, 48);
      const atmosphereInnerMat = new THREE.MeshBasicMaterial({
        color: 0x66aaff,
        transparent: true,
        opacity: 0.20,
        side: THREE.FrontSide,
        depthWrite: false,
      });
      const atmosphereInner = new THREE.Mesh(atmosphereInnerGeo, atmosphereInnerMat);
      atmosphereInner.name = 'atmosphere_inner';
      group.add(atmosphereInner);

      // 云层（带透明度的云层纹理，在地球表面上方）
      const cloudGeometry = new THREE.SphereGeometry(config.radius * 1.015, 48, 48);
      const cloudTextureUrl = '/textures/2k_earth_clouds.jpg';
      const cloudTexture = this.textureLoader.load(cloudTextureUrl);
      const cloudMaterial = new THREE.MeshBasicMaterial({
        map: cloudTexture,
        transparent: true,
        opacity: 0.55,     // ★ 提高云层可见度
        depthWrite: false, // ★ 避免深度冲突
      });
      const clouds = new THREE.Mesh(cloudGeometry, cloudMaterial);
      clouds.name = 'clouds';
      clouds.renderOrder = 1; // ★ 确保云层在地球之后渲染
      group.add(clouds);

      // ★ 添加月球（围绕地球旋转）
      const moonRadius = config.radius * 0.25; // 月球半径约为地球的 1/4
      const moonGeometry = new THREE.SphereGeometry(moonRadius, 32, 32);
      const moonMaterial = new THREE.MeshStandardMaterial({
        color: 0xaaaaaa,  // 灰色
        roughness: 0.8,
        metalness: 0.1,
      });
      const moon = new THREE.Mesh(moonGeometry, moonMaterial);
      moon.name = 'moon';

      // 月球轨道半径（场景单位）
      const moonOrbitRadius = config.radius * 2.5;
      moon.position.x = moonOrbitRadius;

      // 将月球添加到地球的子组，用于轨道动画
      const moonOrbitGroup = new THREE.Group();
      moonOrbitGroup.name = 'moon_orbit';
      moonOrbitGroup.add(moon);
      group.add(moonOrbitGroup);
    }

    // 行星名称标签
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 256, 128);
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = 'rgba(0,0,0,0.9)';
    ctx.lineWidth = 6;
    ctx.strokeText(config.nameCn, 128, 64);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(config.nameCn, 128, 64);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.y = config.radius + 0.8;
    sprite.scale.set(3, 1.5, 1);
    sprite.name = 'label';
    sprite.userData = { config };
    group.add(sprite);

    return group;
  }

  /**
   * 切换语言时重新绘制所有行星标签
   */
  public setLanguage(locale: 'zh' | 'en'): void {
    for (const [, group] of this.planets) {
      const label = group.getObjectByName('label') as THREE.Sprite | undefined;
      const config = label?.userData?.config as PlanetConfig | undefined;
      if (!label || !config) continue;

      const text = locale === 'zh' ? config.nameCn : config.name;
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 128;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, 256, 128);
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = 'rgba(0,0,0,0.9)';
      ctx.lineWidth = 6;
      ctx.strokeText(text, 128, 64);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(text, 128, 64);

      const texture = new THREE.CanvasTexture(canvas);
      const material = label.material as THREE.SpriteMaterial;
      if (material.map) material.map.dispose();
      material.map = texture;
      material.needsUpdate = true;
    }
  }

  /** 添加轨道线 */
  private addOrbitLines(): void {
    for (let i = 1; i < PLANET_CONFIGS.length; i++) {
      const config = PLANET_CONFIGS[i];
      const points: THREE.Vector3[] = [];
      const segments = 128;

      for (let j = 0; j <= segments; j++) {
        const angle = (j / segments) * Math.PI * 2;
        const r = config.orbitRadius;
        const x = r * Math.cos(angle) * (1 - config.orbitEccentricity);
        const z = r * Math.sin(angle);
        points.push(new THREE.Vector3(x, 0, z));
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x5566aa,
        transparent: true,
        opacity: 0.35,
      });

      const line = new THREE.Line(geometry, material);
      this.scene.add(line);
      this.orbitLines.push(line);
    }
  }

  /** 动画循环 */
  public animate(): void {
    requestAnimationFrame(() => this.animate());

    // ★ 使用累积时间 + 帧增量，避免速度切换时位置跳变
    const now = performance.now() / 1000;
    let delta = now - (this._lastFrameTime || now);
    // tab 切换后 delta 可能非常大，限制最大值防止跳变
    if (delta > 0.5) delta = 0.016;
    this._lastFrameTime = now;
    this._simulationTime += delta * this.timeScale;

    const elapsed = this._simulationTime;

    // 行星公转 + 自转
    for (let i = 1; i < PLANET_CONFIGS.length; i++) {
      const config = PLANET_CONFIGS[i];
      const group = this.planets.get(config.name);
      if (!group) continue;

      const orbitSpeed = (2 * Math.PI) / (config.yearPeriod * 0.05);
      const angle = elapsed * orbitSpeed;
      const r = config.orbitRadius;
      const eccentricity = config.orbitEccentricity;

      group.position.x = r * Math.cos(angle) * (1 - eccentricity);
      group.position.z = r * Math.sin(angle);

      const mesh = group.getObjectByName(`${config.name}_mesh`) as THREE.Mesh;
      if (mesh) {
        mesh.rotation.y += 0.01 * (config.rotationPeriod > 0 ? 1 : -1);
      }

      // 地球云层旋转（比地球自转快一点，模拟云层移动）
      if (config.name === 'Earth') {
        const clouds = group.getObjectByName('clouds') as THREE.Mesh;
        if (clouds) {
          clouds.rotation.y += 0.012; // 云层旋转速度稍快
        }

        // ★ 月球轨道动画
        const moonOrbit = group.getObjectByName('moon_orbit') as THREE.Group;
        if (moonOrbit) {
          // 月球公转周期约 27.3 天（在模拟中加快速度）
          const moonOrbitSpeed = (2 * Math.PI) / (27.3 * 0.05);
          const moonAngle = elapsed * moonOrbitSpeed;
          moonOrbit.rotation.y = moonAngle;
        }
      }
    }

    // 太阳自转 + 光晕脉动
    const sunGroup = this.planets.get('Sun');
    if (sunGroup) {
      const sunMesh = sunGroup.getObjectByName('Sun_mesh') as THREE.Mesh;
      if (sunMesh) sunMesh.rotation.y += 0.002;
      const pulse = 1 + Math.sin(elapsed * 2) * 0.03;
      sunGroup.scale.set(pulse, pulse, pulse);
    }

    // 星空旋转
    if (this.starField) {
      this.starField.rotation.y += 0.00005;
    }

    // ★ 流星动画
    if (this.meteors) {
      const positions = this.meteors.geometry.attributes.position.array as Float32Array;
      const velocities = (this.meteors as any).velocities as Float32Array;
      const count = (this.meteors as any).meteorCount;

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        positions[i3]     += velocities[i3]     * this.timeScale;
        positions[i3 + 1] += velocities[i3 + 1] * this.timeScale;
        positions[i3 + 2] += velocities[i3 + 2] * this.timeScale;

        // 如果流星飞出边界，重置到随机位置
        if (Math.abs(positions[i3]) > 150 || positions[i3 + 1] < -50 || Math.abs(positions[i3 + 2]) > 150) {
          positions[i3]     = (Math.random() - 0.5) * 200;
          positions[i3 + 1] = Math.random() * 100 + 20;
          positions[i3 + 2] = (Math.random() - 0.5) * 200;
        }
      }
      this.meteors.geometry.attributes.position.needsUpdate = true;
    }

    // ★ 镜头跟踪选中星球
    //    - controls.target 始终跟随行星（轨道中心）
    //    - 相机位置由 OrbitControls 自动管理，用户可以自由旋转/缩放
    //    - 注意：相机动画（animateCamera）期间暂不跟踪，避免冲突
    if (this.followTarget && !this._isCameraAnimating) {
      const targetGroup = this.planets.get(this.followTarget);
      if (targetGroup) {
        // 平滑更新 controls.target，锁定行星位置
        this.controls.target.lerp(targetGroup.position.clone(), 0.15);
      }
    }

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  // === 公共方法 ===

  public getCamera(): THREE.PerspectiveCamera { return this.camera; }
  public getScene(): THREE.Scene { return this.scene; }
  public getRenderer(): THREE.WebGLRenderer { return this.renderer; }
  public getPlanets(): Map<string, THREE.Group> { return this.planets; }
  public getTimeScale(): number { return this.timeScale; }

  public setTimeScale(scale: number): void {
    this.timeScale = Math.max(-5, Math.min(5, scale));
  }

  public togglePause(): void {
    if (this.timeScale === 0) {
      // 恢复：如果之前保存了速度就恢复，否则回到 1x
      this.timeScale = (this as any)._savedTimeScale || 1;
    } else {
      (this as any)._savedTimeScale = this.timeScale; // 保存当前速度
      this.timeScale = 0;
    }
  }

  public selectPlanet(name: string): void {
    // 如果已经选中该行星，则取消跟踪
    if (this.followTarget === name) {
      this.followTarget = null;
      this.selectedPlanet = null;
      // ★ 触发取消选中回调（隐藏信息面板）
      if (this.onPlanetDeselect) {
        this.onPlanetDeselect();
      }
      return;
    }
    // 选中新行星，启用跟踪模式
    this.selectedPlanet = name;
    this.followTarget = name;

    // 飞到行星旁边
    const group = this.planets.get(name);
    if (group) {
      this.focusOnPlanet(name);
    }

    // ★ 触发信息面板回调
    const config = PLANET_CONFIGS.find(c => c.name === name);
    if (config && this.onPlanetSelect) {
      this.onPlanetSelect(name, config);
    }
  }

  public focusOnPlanet(name: string): void {
    const group = this.planets.get(name);
    if (!group) return;
    const pos = group.position.clone();
    const offset = name === 'Sun' ? new THREE.Vector3(0, 5, 15) : new THREE.Vector3(0, 2, 8);
    this.animateCamera(this.camera.position.clone(), pos.clone().add(offset), pos);
  }

  public resetView(): void {
    this.followTarget = null;  // 清除跟踪目标
    this.selectedPlanet = null;
    // ★ 触发取消选中回调（隐藏信息面板）
    if (this.onPlanetDeselect) {
      this.onPlanetDeselect();
    }
    this.animateCamera(this.camera.position.clone(), new THREE.Vector3(25, 20, 30), new THREE.Vector3(0, 0, 0));
  }

  public onResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  public setOnPlanetSelect(callback: (name: string, config: PlanetConfig) => void): void {
    this.onPlanetSelect = callback;
  }

  public setOnPlanetDeselect(callback: () => void): void {
    this.onPlanetDeselect = callback;
  }

  public getSelectedPlanet(): string | null {
    return this.selectedPlanet;
  }

  /** 注册加载进度回调 */
  public setOnLoadProgress(callback: (progress: number, total: number) => void): void {
    this.onLoadProgress = callback;
  }

  /** 设置加载管理器 */
  private setupLoadingManager(): void {
    this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
      if (this.onLoadProgress) {
        this.onLoadProgress(itemsLoaded, itemsTotal);
      }
    };

    this.loadingManager.onLoad = () => {
      if (this.onLoadProgress) {
        // 加载完成时，传入总数作为已加载数（表示100%）
        const total = PLANET_CONFIGS.length;
        this.onLoadProgress(total, total);
      }
    };
  }

  /** 切换轨道线显示/隐藏 */
  public toggleOrbits(): void {
    this.orbitLines.forEach(line => {
      line.visible = !line.visible;
    });
  }

  // === 私有辅助方法 ===

  private highlightPlanet(group: THREE.Group): void {
    // 高亮效果已移除（用户反馈：选中时不要高亮）
    // 可以在此处添加其他视觉反馈（如名称标签放大等）
  }

  private clearHighlight(group: THREE.Group): void {
    // 高亮效果已移除
  }

  private animateCamera(startPos: THREE.Vector3, endPos: THREE.Vector3, targetPos: THREE.Vector3): void {
    // 动态时长：距离越远，动画越久（1.5~3 秒）
    const distance = startPos.distanceTo(endPos);
    const duration = Math.max(1500, Math.min(3000, distance * 80));

    const startTime = Date.now();
    const startTarget = this.controls.target.clone();
    this._isCameraAnimating = true;  // ★ 标记动画中

    // 弧线控制点：中点向上偏移，形成弧线飞行轨迹
    const midPoint = startPos.clone().lerp(endPos, 0.5);
    const upOffset = Math.max(5, distance * 0.25);
    const controlPoint = new THREE.Vector3(midPoint.x, midPoint.y + upOffset, midPoint.z);

    const animate = () => {
      const progress = Math.min(1, (Date.now() - startTime) / duration);
      // easeInOutCubic：先加速，后减速，更自然
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // 弧线轨迹（二次贝塞尔曲线）
      const t = eased;
      this.camera.position.x = (1-t)*(1-t)*startPos.x + 2*(1-t)*t*controlPoint.x + t*t*endPos.x;
      this.camera.position.y = (1-t)*(1-t)*startPos.y + 2*(1-t)*t*controlPoint.y + t*t*endPos.y;
      this.camera.position.z = (1-t)*(1-t)*startPos.z + 2*(1-t)*t*controlPoint.z + t*t*endPos.z;

      // 视角目标也平滑过渡
      this.controls.target.lerp(targetPos, Math.min(1, eased * 1.2));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this._isCameraAnimating = false;  // ★ 动画结束
      }
    };
    animate();
  }
}
